import type { AnyID, Item } from '@types'
import { Matrix4, Quaternion, Vector3 } from 'three'

import {
  clampPouffDragMatrix,
  isPouffMatrixFree,
} from './pouffDragCollision'

/** Sin exclusiones: el puff colisiona con módulos, brazos y cualquier linkedItem. */
const COLLIDE_ALL = new Set<string>()

const TMP_POS = new Vector3()
const TMP_QUAT = new Quaternion()
const TMP_SCALE = new Vector3()
const TMP_MATRIX = new Matrix4()

export function isPouffGeometryFree(
  matrix: Matrix4,
  item: { width?: number; height?: number; thickness?: number },
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
): boolean {
  return isPouffMatrixFree(
    matrix,
    Number(item.width || 0),
    Number(item.height || 0),
    Number(item.thickness || 0),
    items,
    collisionableIds,
    excludeId,
    undefined,
    COLLIDE_ALL,
  )
}

/**
 * Arrastre libre del puff: solo restricción de no-penetración OBB.
 * Sin snap, sin imán, sin ignorar brazos.
 * Si la posición propuesta penetra, se queda en el último punto libre del rayo
 * desde la posición libre anterior (sin empujones laterales).
 */
export function resolvePouffFreeDragMatrix(
  proposedMatrix: Matrix4,
  previousFreeMatrix: Matrix4 | null,
  item: Item,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
): Matrix4 | null {
  const width = Number(item.width || 0)
  const height = Number(item.height || 0)
  const thickness = Number(item.thickness || 0)
  if (!(width > 0) || !(height > 0) || !(thickness > 0)) return null

  if (
    isPouffMatrixFree(
      proposedMatrix,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
      undefined,
      COLLIDE_ALL,
    )
  ) {
    return proposedMatrix
  }

  const from = previousFreeMatrix ?? item.matrix
  if (!from) return null

  const clamped = clampPouffDragMatrix(
    from,
    proposedMatrix,
    width,
    height,
    thickness,
    items,
    collisionableIds,
    excludeId,
    undefined,
    COLLIDE_ALL,
  )

  if (
    isPouffMatrixFree(
      clamped,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
      undefined,
      COLLIDE_ALL,
    )
  ) {
    return clamped
  }

  // Mantener la última posición libre (sin saltos ni push-out lateral).
  return from.clone()
}

function matrixFromPose(position: Vector3, quaternion: Quaternion): Matrix4 {
  return new Matrix4().compose(position, quaternion, new Vector3(1, 1, 1))
}

/**
 * Valida la pose de inserción del puff. Si penetra, busca en el exterior
 * (desplazamientos en XZ locales) una posición libre sin mover otras unidades.
 */
export function resolveValidPouffInsertPose(
  position: Vector3,
  quaternion: Quaternion,
  item: { width?: number; height?: number; thickness?: number; id?: AnyID },
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
): { position: Vector3; quaternion: Quaternion } | null {
  const width = Number(item.width || 0)
  const height = Number(item.height || 0)
  const thickness = Number(item.thickness || 0)
  if (!(width > 0) || !(height > 0) || !(thickness > 0)) return null

  const excludeId = (item.id as AnyID) || ('' as AnyID)
  const candidate = matrixFromPose(position, quaternion)
  if (isPouffGeometryFree(candidate, item, items, collisionableIds, excludeId)) {
    return { position: position.clone(), quaternion: quaternion.clone() }
  }

  const localX = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const localZ = new Vector3(0, 0, 1).applyQuaternion(quaternion)
  const step = Math.max(40, Math.min(width, thickness) * 0.35)
  const maxRadius = Math.max(width, thickness) * 2.5
  const directions = [
    localX,
    localX.clone().negate(),
    localZ,
    localZ.clone().negate(),
    localX.clone().add(localZ).normalize(),
    localX.clone().sub(localZ).normalize(),
    localX.clone().negate().add(localZ).normalize(),
    localX.clone().negate().sub(localZ).normalize(),
  ]

  for (let dist = step; dist <= maxRadius + 0.5; dist += step) {
    for (const dir of directions) {
      TMP_POS.copy(position).addScaledVector(dir, dist)
      TMP_POS.y = position.y
      TMP_MATRIX.compose(TMP_POS, quaternion, TMP_SCALE.set(1, 1, 1))
      if (isPouffGeometryFree(TMP_MATRIX, item, items, collisionableIds, excludeId)) {
        return { position: TMP_POS.clone(), quaternion: quaternion.clone() }
      }
    }
  }

  return null
}
